export const PERMISSION_SCOPES = ['own', 'team', 'org-unit', 'all'] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/**
 * `company` permissions are bundled into roles of a company. `platform` permissions sit above the
 * companies (ADR-0009) and are only granted by a superadmin, e.g. `platform.superadmin` or the
 * operator permission of a group module (ADR-0011).
 */
export type PermissionLevel = 'company' | 'platform';

export interface PermissionDefinition {
  /** `<module>.<resource>.<action>` */
  key: string;
  /** Omitted means the permission applies to the whole active company without a scope. */
  scopes?: readonly PermissionScope[];
  /** Every access, reads included, is written to the audit log. */
  sensitive?: boolean;
  /** Requires an MFA login and is highlighted in the recertification report. */
  privileged?: boolean;
  level?: PermissionLevel;
}

/** A permission definition together with the module (or kernel area) that declares it. */
export interface CatalogPermission extends PermissionDefinition {
  owner: string;
  level: PermissionLevel;
}

/** One line of a role: a permission key or pattern (`it.*.read`) with the granted scope. */
export interface PermissionGrant {
  permission: string;
  scope: PermissionScope | null;
}

/** Effective permissions: key → broadest granted scope, `null` for unscoped permissions. */
export type PermissionMap = Readonly<Record<string, PermissionScope | null>>;

const SEGMENT = '[a-z][a-z0-9-]*';
const KEY_PATTERN = new RegExp(`^${SEGMENT}(?:\\.${SEGMENT})+$`);
const GRANT_PATTERN = new RegExp(`^${SEGMENT}(?:\\.(?:${SEGMENT}|\\*))+$`);
const SCOPE_RANK: Record<PermissionScope, number> = { own: 1, team: 2, 'org-unit': 3, all: 4 };

export function isPermissionKey(value: string): boolean {
  return KEY_PATTERN.test(value);
}

/** Keys and patterns allowed in roles. `*` stands for one or more segments, never the first. */
export function isPermissionPattern(value: string): boolean {
  return GRANT_PATTERN.test(value);
}

export function matchesPermission(pattern: string, key: string): boolean {
  if (!pattern.includes('*')) return pattern === key;
  const source = pattern
    .split('.')
    .map((segment) => (segment === '*' ? `${SEGMENT}(?:\\.${SEGMENT})*` : segment))
    .join('\\.');
  return new RegExp(`^${source}$`).test(key);
}

export function compareScopes(a: PermissionScope, b: PermissionScope): number {
  return SCOPE_RANK[a] - SCOPE_RANK[b];
}

/** The broadest scope the permission supports that does not exceed the requested one. */
function fitScope(
  definition: PermissionDefinition,
  requested: PermissionScope | null,
): PermissionScope | null | undefined {
  if (!definition.scopes?.length) return null;
  if (requested === null) return undefined;
  const fitting = definition.scopes.filter((scope) => compareScopes(scope, requested) <= 0);
  return fitting.sort(compareScopes).at(-1);
}

/**
 * Expands role grants against the permission catalog. Wildcards are resolved here and nowhere
 * else: code always checks concrete keys. Scoped permissions without a fitting scope are not
 * granted (deny by default).
 */
export function resolveGrants(
  grants: Iterable<PermissionGrant>,
  catalog: readonly CatalogPermission[],
  level: PermissionLevel,
): Record<string, PermissionScope | null> {
  const result: Record<string, PermissionScope | null> = {};
  for (const grant of grants) {
    for (const definition of catalog) {
      if (definition.level !== level || !matchesPermission(grant.permission, definition.key)) {
        continue;
      }
      const scope = fitScope(definition, grant.scope);
      if (scope === undefined) continue;
      const current = result[definition.key];
      if (
        current === undefined ||
        (scope !== null && current !== null && compareScopes(scope, current) > 0)
      ) {
        result[definition.key] = scope;
      }
    }
  }
  return result;
}

/** Every permission of the given level with its broadest scope, used for the superadmin. */
export function allPermissions(
  catalog: readonly CatalogPermission[],
  level: PermissionLevel,
): Record<string, PermissionScope | null> {
  return resolveGrants(
    catalog.filter((p) => p.level === level).map((p) => ({ permission: p.key, scope: 'all' })),
    catalog,
    level,
  );
}

export function hasPermission(map: PermissionMap, key: string, scope?: PermissionScope): boolean {
  if (!(key in map)) return false;
  const granted = map[key];
  if (scope === undefined || granted === null || granted === undefined) return true;
  return compareScopes(granted, scope) >= 0;
}

/**
 * Privileged and sensitive permissions a role would grant. Such a role is never assigned to every
 * account of a company (docs/security/permissions.md).
 */
export function notForEveryone(
  grants: readonly Pick<PermissionGrant, 'permission'>[],
  catalog: readonly PermissionDefinition[],
): string[] {
  return catalog
    .filter(
      (p) =>
        (p.privileged || p.sensitive) &&
        grants.some((grant) => matchesPermission(grant.permission, p.key)),
    )
    .map((p) => p.key);
}

/** i18n key prefix of a permission: `<owner>.permissions.<rest>` with `.label` and `.description`. */
export function permissionI18nKey(permission: Pick<CatalogPermission, 'key' | 'owner'>): string {
  return `${permission.owner}.permissions.${permission.key.slice(permission.owner.length + 1)}`;
}
